import { useMutation, useApolloClient } from '@apollo/client';
import {
    getProductionPlanningTemplatesTemplate,
    saveProductionPlanningTemplateTemplate,
    updateProductionPlanningTemplateTemplate,
    deleteProductionPlanningTemplateTemplate,
} from 'apollo/templates/organization';
import { ProductionPlanningTemplateInput } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useCallback, useEffect, useState } from 'react';

export const usePlanningTemplate = () => {
    const { showBoundary } = useDenthubErrorBoundary();

    const client = useApolloClient();
    const [planningTemplates, setPlanningTemplates] = useState<any[]>([]);
    const [planningTemplatesLoading, setPlanningTemplatesLoading] = useState(true);

    const fetchTemplates = useCallback(async () => {
        setPlanningTemplatesLoading(true);
        const { data } = await client.query({
            query: getProductionPlanningTemplatesTemplate,
            fetchPolicy: 'no-cache',
        });
        setPlanningTemplates(data?.productionPlanningTemplates ?? []);
        setPlanningTemplatesLoading(false);
    }, [client]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const [addTemplate, { loading: addTemplateLoading }] = useMutation(saveProductionPlanningTemplateTemplate);
    const addPlanningTemplate = async (variables: ProductionPlanningTemplateInput) => {
        try {
            const result = await addTemplate({ variables });
            await fetchTemplates();
            return result.data.saveProductionPlanningTemplate;
        } catch (error) {
            showBoundary(error);
        }
    };

    const [editTemplate, { loading: editTemplateLoading }] = useMutation(updateProductionPlanningTemplateTemplate);
    const editPlanningTemplate = async (variables: ProductionPlanningTemplateInput) => {
        try {
            const result = await editTemplate({ variables });
            await fetchTemplates();
            return result.data.updateProductionPlanningTemplate;
        } catch (error) {
            showBoundary(error);
        }
    };

    const [deleteTemplate, { loading: deleteTemplateLoading }] = useMutation(deleteProductionPlanningTemplateTemplate, {
        onCompleted() {
            fetchTemplates();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const deletePlanningTemplate = async (variables: { templateId: string }) => {
        return deleteTemplate({ variables }).then((result) => result.data?.deleteProductionPlanningTemplate);
    };

    return {
        planningTemplates,
        planningTemplatesLoading,
        refetchPlanningTemplates: fetchTemplates,

        addPlanningTemplate,
        editPlanningTemplate,
        deletePlanningTemplate,

        addTemplateLoading,
        editTemplateLoading,
        deleteTemplateLoading,
    };
};
